import React, { useState, useEffect } from 'react';
import { Bluetooth, Usb, RefreshCw, Loader2, AlertCircle, Bug, ChevronDown, ChevronUp, Copy, Download } from 'lucide-react';
import { 
  startPrinterDiscovery, 
  stopPrinterDiscovery, 
  getDiscoveredAndroidPrinters, 
  connectAndroidPrinter, 
  getAndroidConnectionStatus,
  sendAndroidRawCommand,
  addDiscoveryListener,
  removeAllDiscoveryListeners,
  DiscoveredPrinter 
} from '../utils/printerBridge';

import { OKI_PAPER_SIZES } from '../types';

interface AndroidPrinterSelectProps {
  onConnected: (settings: {
    language: 'TSPL' | 'CPCL' | 'ESC';
    rotate: boolean;
    driverType: 'android_tspl' | 'godex' | 'oki_usb';
    // Godex Settings
    godexPdfMode?: 'sdk' | 'gepl' | 'legacy_bitmap' | 'ezpl_hex';
    // Oki Settings
    okiMediaSource?: 'AUTO' | 'TRAY1' | 'TRAY2' | 'MPTRAY' | 'MANUALFEED';
    okiMediaWeight?: 'LIGHT' | 'MEDIUM' | 'HEAVY' | 'ULTRAHEAVY';
    okiMediaType?: 'PLAIN' | 'LABELS' | 'BOND' | 'RECYCLED' | 'CARDSTOCK' | 'ROUGH';
    okiPaperSize?: string;
  }) => void;
  onClose: () => void;
  darkMode: boolean;
}

export const AndroidPrinterSelect: React.FC<AndroidPrinterSelectProps> = ({ onConnected, onClose, darkMode }) => {
  const [printers, setPrinters] = useState<DiscoveredPrinter[]>([]);
  const [scanning, setScanning] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectedPrinter, setConnectedPrinter] = useState<{ connected: boolean; type?: string }>({ connected: false });
  
  // Debug state
  const [debugMode, setDebugMode] = useState(false);
  const [debugLogs, setDebugLogs] = useState<string[]>([]);
  const [showDebugLogs, setShowDebugLogs] = useState(true);
  const [rawCommand, setRawCommand] = useState('^L\nE');
  const [rawSending, setRawSending] = useState(false);
  const [rawResult, setRawResult] = useState<string | null>(null);
  
  // Settings
  const [language, setLanguage] = useState<'TSPL' | 'CPCL' | 'ESC'>('TSPL');
  const [driverType, setDriverType] = useState<'android_tspl' | 'godex' | 'oki_usb'>('android_tspl');
  const [rotate, setRotate] = useState(true);
  const [godexPdfMode, setGodexPdfMode] = useState<'sdk' | 'gepl' | 'legacy_bitmap' | 'ezpl_hex'>('ezpl_hex');

  // Oki Settings
  const [okiMediaSource, setOkiMediaSource] = useState<'AUTO' | 'TRAY1' | 'TRAY2' | 'MPTRAY' | 'MANUALFEED'>('AUTO');
  const [okiMediaWeight, setOkiMediaWeight] = useState<'LIGHT' | 'MEDIUM' | 'HEAVY' | 'ULTRAHEAVY'>('ULTRAHEAVY');
  const [okiMediaType, setOkiMediaType] = useState<'PLAIN' | 'LABELS' | 'BOND' | 'RECYCLED' | 'CARDSTOCK' | 'ROUGH'>('LABELS');
  const [okiPaperSize, setOkiPaperSize] = useState<string>('A4');


  const logStep = (message: string, isError: boolean = false) => {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    const logMessage = `[${timestamp}] ${message}`;
    console.log(logMessage);
    setDebugLogs(prev => [...prev, logMessage]);
    if (isError) {
      setError(message);
    }
  };

  const checkConnection = async () => {
    try {
      const status = await getAndroidConnectionStatus();
      setConnectedPrinter(status);
    } catch (e) {
      console.error('Failed to check connection status', e);
    }
  };

  useEffect(() => {
    console.log('AndroidPrinterSelect mounted');
    logStep('Component mounted');
    checkConnection();
    
    // Add USB printer immediately
    setPrinters(prev => {
      if (!prev.some(p => p.type === 'usb')) {
        return [...prev, { name: 'USB Printer', address: 'usb', type: 'usb' }];
      }
      return prev;
    });

    return () => {
      console.log('AndroidPrinterSelect unmounting');
      logStep('Component unmounting');
      stopScan();
      removeAllDiscoveryListeners();
    };
  }, []);

  const stopScan = async () => {
    try {
      await stopPrinterDiscovery();
      setScanning(false);
    } catch (e) {
      console.error('Stop scan failed', e);
    }
  };

  const handleSendRawCommand = async () => {
    if (driverType !== 'godex') {
      setRawResult('Raw commands are only supported for Godex driver.');
      return;
    }
    setRawSending(true);
    setRawResult(null);
    try {
      const res = await sendAndroidRawCommand({ command: rawCommand, driverType: 'godex' });
      setRawResult(res?.message || 'Command sent');
    } catch (e: any) {
      setRawResult(e?.message || 'Failed to send command');
    } finally {
      setRawSending(false);
    }
  };

  // COPY THE EXACT PATTERN FROM WORKING scanBluetooth IN APP.TSX
  const startScan = async () => {
    logStep('User clicked Scan Again');
    setScanning(true);
    setPrinters(prev => prev.filter(p => p.type === 'usb')); // Keep USB, clear BT
    setError(null);
    
    try {
      logStep('Setting up discovery listener');
      addDiscoveryListener('onDeviceFound', (device: DiscoveredPrinter) => {
        logStep(`Device found: ${device.name} (${device.address})`);
        setPrinters(prev => {
          if (prev.some(p => p.address === device.address)) return prev;
          return [...prev, device];
        });
      });
      
      logStep('Calling startPrinterDiscovery()');
      await startPrinterDiscovery();
      logStep('Discovery started successfully');
      
      // Auto-stop after 15s (same as working version)
      setTimeout(async () => {
        logStep('Auto-stopping scan after 15 seconds');
        await stopPrinterDiscovery();
        setScanning(false);
      }, 15000);

    } catch (e: any) {
      logStep(`Scan error: ${e.message || 'Unknown error'}`, true);
      setScanning(false);
    }
  };

  const handleConnect = async (printer: DiscoveredPrinter) => {
    logStep(`Attempting to connect to ${printer.name}`);
    setConnecting(true);
    setError(null);
    
    try {
      if (printer.type === 'usb') {
        logStep('USB connection - requesting permission');
        await connectAndroidPrinter({ ...printer, driverType });
        await new Promise(r => setTimeout(r, 500));
        
        // Retry logic for USB
        let retries = 0;
        while (retries < 3) {
          logStep(`USB connection attempt ${retries + 1}/3`);
          try {
            await connectAndroidPrinter({ ...printer, driverType });
            break;
          } catch (retryErr) {
            retries++;
            if (retries >= 3) throw retryErr;
            await new Promise(r => setTimeout(r, 1000));
          }
        }
      } else {
        logStep('Bluetooth connection');
        await connectAndroidPrinter({ ...printer, driverType });
      }
      
      logStep('Connection successful!');
      await checkConnection();
      
      // Call parent callback
      onConnected({
        language,
        rotate,
        driverType,
        ...(driverType === 'godex' ? {
          godexPdfMode
        } : {}),
        ...(driverType === 'oki_usb' ? {
          okiMediaSource,
          okiMediaWeight,
          okiMediaType,
          okiPaperSize
        } : {})
      });
      
    } catch (e: any) {
      logStep(`Connection failed: ${e.message || 'Unknown error'}`, true);
    } finally {
      setConnecting(false);
    }
  };

  // Helper to get dropdown/input styles based on theme
  const inputStyle = darkMode 
    ? 'bg-slate-900 border-slate-700 text-slate-300 focus:border-blue-500' 
    : 'bg-white border-slate-200 text-slate-600 focus:border-blue-500';

  return (
    <div 
      id="scan-modal-backdrop"
      className="fixed inset-0 z-[150] flex items-center justify-center bg-black/80 p-4"
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
    >
      <div 
        className={`w-full max-w-md rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] ${darkMode ? 'bg-slate-900 text-white' : 'bg-white text-slate-900'}`}
        onClick={e => e.stopPropagation()}
      >
        <div className={`p-4 border-b flex justify-between items-center ${darkMode ? 'border-slate-800' : 'border-slate-100'}`}>
          <h3 className="font-bold text-lg flex items-center gap-2">
            Printer Setup
            {scanning && <Loader2 className="w-4 h-4 animate-spin text-blue-500"/>}
          </h3>
          <button onClick={onClose} className="text-sm font-medium opacity-70 hover:opacity-100">Cancel</button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto min-h-[300px]">
          {/* GODEX MODE - AT THE VERY TOP */}
          <div className="mb-4 p-4 bg-yellow-400 border-4 border-black rounded-xl">
            <div className="text-black font-black text-base mb-2">GODEX PRINT MODE</div>
            <select
              value={godexPdfMode}
              onChange={(e) => setGodexPdfMode(e.target.value as any)}
              className="w-full p-3 text-base font-bold border-2 border-black rounded bg-white text-black"
            >
              <option value="ezpl_hex">EZPL (Recommended)</option>
              <option value="sdk">SDK printPDF</option>
              <option value="legacy_bitmap">Legacy Bitmap</option>
              <option value="gepl">GEPL Binary</option>
            </select>
            <div className="mt-2 text-sm text-black">Selected: {godexPdfMode}</div>
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm mb-3 flex items-center gap-2 border border-red-100">
              <AlertCircle className="w-4 h-4"/> {error}
            </div>
          )}

          {/* SETTINGS SECTION */}
          <div className={`mb-6 p-3 rounded-lg border ${darkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
            <h4 className="text-xs font-bold uppercase tracking-wider opacity-50 mb-3">Print Settings</h4>
            
            <div className="space-y-4">
                <div>
                    <label className="text-xs font-bold block mb-1.5">Printer Driver</label>
                    <div className="grid grid-cols-3 gap-2">
                        {[
                            { id: 'android_tspl', label: 'Generic (TSPL)' },
                            { id: 'godex', label: 'Godex (SDK)' },
                            { id: 'oki_usb', label: 'OKI Laser' }
                        ].map((driver) => (
                            <button
                                key={driver.id}
                                onClick={() => setDriverType(driver.id as any)}
                                className={`py-2 text-xs font-bold rounded border transition-all ${
                                    driverType === driver.id
                                        ? 'bg-blue-600 border-blue-600 text-white' 
                                        : (darkMode ? 'bg-slate-900 border-slate-700 text-slate-400' : 'bg-white border-slate-200 text-slate-600')
                                }`}
                            >
                                {driver.label}
                            </button>
                        ))}
                    </div>
                </div>


                {driverType === 'oki_usb' ? (
                  // OKI SPECIFIC SETTINGS
                  <div className="space-y-3 pt-2 border-t border-slate-700/20">
                     <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-bold block mb-1.5">Paper Size</label>
                          <select 
                            value={okiPaperSize}
                            onChange={(e) => setOkiPaperSize(e.target.value)}
                            className={`w-full text-xs p-2 rounded border outline-none ${inputStyle}`}
                          >
                            {Object.entries(OKI_PAPER_SIZES).map(([key, val]) => (
                              <option key={key} value={key}>{val.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-bold block mb-1.5">Media Type</label>
                          <select 
                            value={okiMediaType}
                            onChange={(e) => setOkiMediaType(e.target.value as any)}
                            className={`w-full text-xs p-2 rounded border outline-none ${inputStyle}`}
                          >
                            <option value="PLAIN">Plain</option>
                            <option value="LABELS">Labels</option>
                            <option value="BOND">Bond</option>
                            <option value="RECYCLED">Recycled</option>
                            <option value="CARDSTOCK">Cardstock</option>
                            <option value="ROUGH">Rough</option>
                          </select>
                        </div>
                     </div>
                     <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-bold block mb-1.5">Media Weight</label>
                          <select 
                            value={okiMediaWeight}
                            onChange={(e) => setOkiMediaWeight(e.target.value as any)}
                            className={`w-full text-xs p-2 rounded border outline-none ${inputStyle}`}
                          >
                            <option value="LIGHT">Light</option>
                            <option value="MEDIUM">Medium</option>
                            <option value="HEAVY">Heavy</option>
                            <option value="ULTRAHEAVY">Ultra Heavy</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-bold block mb-1.5">Source Tray</label>
                          <select 
                            value={okiMediaSource}
                            onChange={(e) => setOkiMediaSource(e.target.value as any)}
                            className={`w-full text-xs p-2 rounded border outline-none ${inputStyle}`}
                          >
                            <option value="AUTO">Auto Select</option>
                            <option value="TRAY1">Tray 1</option>
                            <option value="TRAY2">Tray 2</option>
                            <option value="MPTRAY">MP Tray</option>
                            <option value="MANUALFEED">Manual Feed</option>
                          </select>
                        </div>
                     </div>
                  </div>
                ) : (
                  // STANDARD THERMAL SETTINGS
                  driverType !== 'godex' && (
                    <div>
                        <label className="text-xs font-bold block mb-1.5">Printer Language</label>
                        <div className="grid grid-cols-3 gap-2">
                            {['TSPL', 'CPCL', 'ESC'].map((lang) => (
                                <button
                                    key={lang}
                                    onClick={() => setLanguage(lang as any)}
                                    className={`py-2 text-xs font-bold rounded border transition-all ${
                                        language === lang 
                                            ? 'bg-blue-600 border-blue-600 text-white' 
                                            : (darkMode ? 'bg-slate-900 border-slate-700 text-slate-400' : 'bg-white border-slate-200 text-slate-600')
                                    }`}
                                >
                                    {lang}
                                </button>
                            ))}
                        </div>
                        <p className="text-[10px] mt-1 opacity-50">Most thermal printers use TSPL or CPCL.</p>
                    </div>
                  )
                )}

                {driverType !== 'oki_usb' && (
                  <div className="flex items-center justify-between py-2 border-t border-slate-700/20">
                      <div>
                          <label className="text-xs font-bold block">Rotate Print 90°</label>
                          <p className="text-[10px] opacity-50 text-wrap pr-4">Required for long plates on narrow thermal paper.</p>
                      </div>
                      <input 
                          type="checkbox" 
                          checked={rotate} 
                          onChange={e => setRotate(e.target.checked)}
                          className="w-5 h-5 accent-blue-600"
                      />
                  </div>
                )}
            </div>
            </div>

          <h4 className="text-xs font-bold uppercase tracking-wider opacity-50 mb-2">Available Printers</h4>
          <div className="space-y-2">
            {printers.map((printer, idx) => (
              <button
                key={`${printer.address}-${idx}`}
                onClick={() => handleConnect(printer)}
                disabled={connecting}
                className={`w-full p-3 rounded-lg border text-left flex items-center gap-3 transition-all ${
                  darkMode 
                    ? 'border-slate-700 hover:bg-slate-800' 
                    : 'border-slate-200 hover:bg-slate-50'
                } ${connecting ? 'opacity-50' : ''}`}
              >
                <div className={`p-2 rounded-full ${darkMode ? 'bg-slate-800' : 'bg-slate-100'}`}>
                  {printer.type === 'usb' ? <Usb className="w-5 h-5"/> : <Bluetooth className="w-5 h-5"/>}
                </div>
                <div className="flex-1">
                  <div className="font-bold text-sm">{printer.name || 'Unknown Device'}</div>
                  <div className="text-xs opacity-60 font-mono">{printer.address}</div>
                </div>
                {connecting && <Loader2 className="w-4 h-4 animate-spin"/>}
              </button>
            ))}

            {printers.length === 0 && !scanning && (
              <div className="text-center py-8 opacity-50 text-sm">
                No printers found. Make sure Bluetooth is on.
              </div>
            )}
          </div>
        </div>

        {/* DEBUG MODE SECTION - ALWAYS VISIBLE AT BOTTOM */}
        <div className={`p-3 border-t ${darkMode ? 'border-slate-800 bg-slate-900/50' : 'border-slate-200 bg-slate-50'}`}>
          <button
            onClick={() => setDebugMode(!debugMode)}
            className={`w-full flex items-center justify-between p-2 rounded-lg text-xs font-bold transition-colors ${
              debugMode 
                ? 'bg-blue-600 text-white' 
                : darkMode 
                  ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' 
                  : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
            }`}
          >
            <span className="flex items-center gap-2">
              <Bug className="w-4 h-4" />
              Debug Mode
            </span>
            {debugMode ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {debugMode && (
            <div className="mt-3 space-y-2">
              {/* Debug Logs */}
              <button
                onClick={() => setShowDebugLogs(!showDebugLogs)}
                className={`w-full flex items-center justify-between p-2 rounded-lg text-xs font-medium ${
                  darkMode ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                }`}
              >
                <span>Debug Logs ({debugLogs.length})</span>
                {showDebugLogs ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>

              {showDebugLogs && debugLogs.length > 0 && (
                <div className={`p-3 rounded-lg max-h-48 overflow-y-auto text-xs font-mono ${darkMode ? 'bg-slate-950 border border-slate-700' : 'bg-slate-100 border border-slate-300'}`}>
                  {debugLogs.map((log, idx) => (
                    <div 
                      key={idx} 
                      className={`mb-1 ${log.includes('error') || log.includes('failed') ? 'text-red-600 dark:text-red-400' : 'text-slate-600 dark:text-slate-400'}`}
                    >
                      {log}
                    </div>
                  ))}
                </div>
              )}

              {/* Copy Logs Button */}
              <button
                onClick={() => {
                  const logText = debugLogs.join('\n');
                  navigator.clipboard.writeText(logText).then(() => {
                    alert('Debug logs copied to clipboard');
                  });
                }}
                className={`w-full flex items-center justify-center gap-2 p-2 rounded-lg text-xs font-medium ${
                  darkMode ? 'bg-slate-800 text-slate-300 hover:bg-slate-700' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                }`}
              >
                <Copy className="w-4 h-4" />
                Copy Debug Logs
              </button>

              {/* GODEX PRINT MODE SELECTOR - Always visible */}
              <div className={`p-3 rounded-lg border ${darkMode ? 'bg-yellow-900/30 border-yellow-700' : 'bg-yellow-50 border-yellow-300'}`}>
                <div className="text-xs font-bold mb-2">Godex Print Mode {driverType !== 'godex' && '(select Godex driver first)'}</div>
                <select
                  value={godexPdfMode}
                  onChange={(e) => setGodexPdfMode(e.target.value as any)}
                  disabled={driverType !== 'godex'}
                  className={`w-full text-xs p-2 rounded border outline-none mb-2 ${inputStyle} ${driverType !== 'godex' ? 'opacity-50' : ''}`}
                >
                  <option value="ezpl_hex">EZPL (putImage)</option>
                  <option value="sdk">SDK (printPDF)</option>
                  <option value="legacy_bitmap">Legacy Bitmap</option>
                  <option value="gepl">GEPL Binary</option>
                </select>
                <p className="text-[10px] opacity-70">Selected: {godexPdfMode} | Driver: {driverType}</p>
              </div>

              <div className={`p-3 rounded-lg border ${darkMode ? 'bg-slate-950 border-slate-700' : 'bg-white border-slate-300'}`}>
                <div className="text-xs font-bold mb-2">Godex Raw Command</div>
                <textarea
                  value={rawCommand}
                  onChange={(e) => setRawCommand(e.target.value)}
                  className={`w-full text-xs font-mono p-2 rounded border outline-none ${inputStyle}`}
                  rows={3}
                />
                <button
                  onClick={handleSendRawCommand}
                  disabled={rawSending}
                  className={`mt-2 w-full p-2 rounded-lg text-xs font-bold ${
                    rawSending
                      ? 'opacity-60 bg-blue-600 text-white'
                      : 'bg-blue-600 hover:bg-blue-500 text-white'
                  }`}
                >
                  {rawSending ? 'Sending…' : 'Send Command'}
                </button>
                {rawResult && (
                  <div className={`mt-2 text-[11px] font-mono whitespace-pre-wrap ${darkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    {rawResult}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* SCAN BUTTON - ALWAYS VISIBLE */}
        <div className={`p-4 border-t flex justify-between items-center ${darkMode ? 'border-slate-800' : 'border-slate-100'}`}>
           <button 
             onClick={startScan} 
             disabled={scanning || connecting}
             className={`flex items-center gap-2 text-sm font-bold ${scanning ? 'opacity-50' : 'hover:opacity-70'}`}
           >
             <RefreshCw className={`w-4 h-4 ${scanning ? 'animate-spin' : ''}`}/> Scan Again
           </button>
           
           {connectedPrinter.connected && (
              <button
                onClick={() => onConnected({
                  language,
                  rotate,
                  driverType,
                  ...(driverType === 'godex' ? {
                    godexPdfMode
                  } : {}),
                  ...(driverType === 'oki_usb' ? {
                    okiMediaSource,
                    okiMediaWeight,
                    okiMediaType,
                    okiPaperSize
                  } : {})
                })}
                className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-lg"
              >
                Continue Printing
              </button>
           )}
        </div>
      </div>
    </div>
  );
};

export default AndroidPrinterSelect;
